import { forwardRef } from 'react'

import { tv } from 'tailwind-variants'

import { Icon } from '@/components/atoms'

export const countrySelectVariants = tv({
  slots: {
    containerSlot: 'relative',
    selectSlot:
      'text-surface-white-400 placeholder:text-surface-white-400 border-surface-white-300 focus:border-surface-white-400 focus:ring-surface-white-400 bg-surface-white-50 font-body w-full cursor-pointer appearance-none rounded-sm border px-4 pr-10 font-normal outline-none ring-0',
    flagContainerSlot:
      'pointer-events-none absolute left-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center',
    selectWithFlagSlot: 'pl-[48px]',
    iconSlot:
      'text-surface-white-400 pointer-events-none absolute bottom-0 right-3.5 top-0 flex items-center justify-center'
  },
  variants: {
    size: {
      sm: {
        selectSlot: 'text-caption-sm h-10 py-2'
      },
      md: {
        selectSlot: 'text-body-md h-12 py-3'
      },
      lg: {
        selectSlot: 'text-body-lg h-14 py-4'
      }
    },
    error: {
      true: {
        selectSlot:
          'border-danger-100 text-danger-200 focus:border-danger-200 focus:ring-danger-100 font-semibold focus:ring-1',
        iconSlot: 'text-danger-200'
      }
    },
    disabled: {
      true: {
        selectSlot:
          'bg-surface-white-200 text-surface-white-400 border-surface-white-300 cursor-not-allowed opacity-60',
        iconSlot: 'text-surface-white-400 opacity-60'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

export interface TCountry {
  name: string
  code: string
}

export type TCountrySelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> & {
  countries: TCountry[]
  error?: string
  placeholder?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  helperTextId?: string
  errorTextId?: string
}

const getFlagEmoji = (countryCode: string) => {
  if (!countryCode || countryCode.length !== 2) return ''
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

export const CountrySelect = forwardRef<HTMLSelectElement, TCountrySelectProps>(
  (
    {
      countries,
      placeholder,
      className,
      size,
      error,
      disabled,
      helperTextId,
      errorTextId,
      value,
      ...props
    },
    ref
  ) => {
    const { name, id, ...restProps } = props

    const describedBy =
      [helperTextId, errorTextId].filter(Boolean).join(' ') || undefined

    const {
      containerSlot,
      selectSlot,
      flagContainerSlot,
      selectWithFlagSlot,
      iconSlot
    } = countrySelectVariants({
      size,
      error: !!error,
      disabled: !!disabled
    })

    const selectedValue = value ?? ''
    const selectedCountry = countries.find(
      country => country.code === selectedValue
    )

    return (
      <div className={containerSlot()}>
        {selectedCountry && (
          <div className={flagContainerSlot()}>
            <span className='text-[14px] leading-none'>
              {getFlagEmoji(selectedCountry.code)}
            </span>
          </div>
        )}
        <select
          className={`${selectSlot({ className })} ${
            selectedCountry ? selectWithFlagSlot() : ''
          }`}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          ref={ref}
          name={name}
          id={id || name}
          {...(value !== undefined
            ? { value: selectedValue }
            : { defaultValue: '' })}
          {...restProps}
        >
          {placeholder && (
            <option value='' disabled>
              {placeholder}
            </option>
          )}
          {countries.map((country, index) => (
            <option key={`${country.code}-${index}`} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        <div className={iconSlot()}>
          <Icon name='keyboard_arrow_down' size='md' />
        </div>
      </div>
    )
  }
)

CountrySelect.displayName = 'CountrySelect'
